import moment from "moment"
import { CampingStyleProps, CampProps, NewReservationProps, ReservationProps, SiteProps } from "../../../interfaces"
import { SiteOption } from "./SitesDropdown"
import { CAMPING_STYLES } from "~/constants"
import parseDateTime from "~/utils/parseDateTime"

type CampingStyleMap = Record<string, string>

const createCampingStyleMap = (campingStyles: CampingStyleProps[]): CampingStyleMap => {
  return campingStyles.reduce((acc, cs) => {
    acc[cs.id] = cs.name
    return acc
  }, {} as CampingStyleMap)
}

export const sortedSites = (
  a: { sort: number; camping_style_id: string },
  b: { sort: number; camping_style_id: string },
  campingStyles: CampingStyleProps[],
) => {
  const campingStyleMap = createCampingStyleMap(campingStyles)

  const aStyleName = campingStyleMap[a.camping_style_id]
  const bStyleName = campingStyleMap[b.camping_style_id]
  const rvName = CAMPING_STYLES.RV

  if (aStyleName === rvName && bStyleName !== rvName) {
    return -1
  } else if (aStyleName !== rvName && bStyleName === rvName) {
    return 1
  } else {
    return a.sort - b.sort
  }
}

const generateSiteOptions = (
  sites: SiteProps[] | undefined,
  reservation: ReservationProps | NewReservationProps | undefined,
  reservationStates: Record<string, { persisted: ReservationProps; updated?: ReservationProps }> | undefined,
  campingStyles: CampingStyleProps[],
  camp: CampProps
): SiteOption[] => {
  if (!sites || !reservation || !reservationStates) return []

  const reservationsBySiteId: Record<string, ReservationProps[]> = {}

  Object.values(reservationStates).forEach((reservationState) => {
    const res = reservationState.updated || reservationState.persisted
    const siteId = res.site_id as string
    if (!reservationsBySiteId[siteId]) {
      reservationsBySiteId[siteId] = []
    }
    reservationsBySiteId[siteId].push(res)
  })


  const reservationStart = reservation?.start_date 
    ? moment.isMoment(reservation.start_date)
      ? reservation.start_date.clone() 
      : parseDateTime(reservation.start_date, camp.timezone, {asMoment: true})
    : null;

  const reservationEnd = reservation?.end_date 
    ? moment.isMoment(reservation.end_date)
      ? reservation.end_date.clone() 
      : parseDateTime(reservation.end_date, camp.timezone, {asMoment: true})
    : null;

  return sites
    .sort((a, b) => sortedSites(a, b, campingStyles))
    .map((site) => {
      if (reservationStart && reservationEnd) {
        const isAvailable = (reservationsBySiteId[site.id] || []).every((res) => {
          const isSameReservation = res.id === reservation.id
          const isReservationProps = (
            reservation: ReservationProps | NewReservationProps,
          ): reservation is ReservationProps => {
            return (reservation as ReservationProps).long_term_reservation_id !== undefined
          }

          const isAffiliated = isReservationProps(reservation)
            ? res.long_term_reservation_id === reservation.long_term_reservation_id &&
              res.site_id === reservation.site_id &&
              res.kind !== reservation.kind
            : false

          const resStartRaw = res.start_date;
          const resEndRaw = res.end_date;
        
          const hasOverlap = reservationStart.isBefore(resStartRaw, "second") && reservationEnd.isAfter(resEndRaw, "second")
          return isSameReservation || isAffiliated || !hasOverlap
        })

        return {
          key: site.id,
          value: site.id,
          title: site.name,
          description: isAvailable ? "" : "Unavailable for dates",
          site: site,
          isDisabled: !isAvailable,
        }
      } else {
        return {
          key: site.id,
          value: site.id,
          title: site.name,
          site: site,
          isDisabled: false,
        }
      }
    })
}

export default generateSiteOptions
